import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTonTransaction } from '../../../hooks/useTonTransaction';
import { fetchLinkedWallet, saveLinkedWallet, unlinkWallet } from '../store/walletSlice';

/**
 * Single source of truth for the user's linked TON wallet.
 *
 * The linked address is persisted on the backend and mirrored in Redux, so every
 * screen with a "Connect wallet" button (Lucky Spin, TON Withdrawal, …) reads the
 * same state and stays in sync. The live TonConnect SDK connection (`tonConnected`)
 * is only relevant for opening the wallet picker — it must NOT drive UI on its own,
 * because it is per-session/per-device and drifts from the persisted link.
 */
export function useLinkedWallet() {
  const dispatch = useDispatch();
  const linkedWallet = useSelector((state) => state.wallet.linkedWallet) || '';

  const {
    isConnected: tonConnected,
    walletAddress: tonAddress,
    ensureWalletConnection,
    disconnect,
  } = useTonTransaction();

  // Load the persisted wallet once per mount so a freshly-opened page reflects a
  // link made earlier (or on another screen) even after a reload reset Redux.
  useEffect(() => {
    dispatch(fetchLinkedWallet());
  }, [dispatch]);

  const connect = useCallback(async () => {
    const wallet = await ensureWalletConnection();
    const address = wallet?.account?.address;
    if (address) {
      await dispatch(saveLinkedWallet(address)).unwrap();
    }
    return address;
  }, [ensureWalletConnection, dispatch]);

  const unlink = useCallback(async () => {
    // Disconnect the SDK first so the live connection can't immediately re-link,
    // then clear the persisted wallet.
    try {
      await disconnect();
    } catch (err) {
      // Already disconnected / nothing to do — still clear the backend link below.
    }
    await dispatch(unlinkWallet()).unwrap();
  }, [disconnect, dispatch]);

  return {
    linkedWallet,
    isWalletLinked: !!linkedWallet,
    tonConnected,
    tonAddress,
    connect,
    unlink,
  };
}

export default useLinkedWallet;
